import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

// ── Module codes (7 system modules) ──────────────────────────────────────────

export const ModuleCodeSchema = z.enum([
  'PRODUCT_MANAGEMENT',
  'TENANT_MANAGEMENT',
  'USER_MANAGEMENT',
  'ENTERPRISES_MANAGEMENT',
  'SETTINGS',
  'RECALL',
  'AUDIT_LOG',
]);
export type ModuleCode = z.infer<typeof ModuleCodeSchema>;

// ── Role codes (3 system roles) ──────────────────────────────────────────────

export const SystemRoleCodeSchema = z.enum(['ADMIN', 'PRODUCTION_SUPPORT', 'PRODUCT_TEAM', 'DEFAULT']);
export type SystemRoleCode = z.infer<typeof SystemRoleCodeSchema>;

// ── Module access map: { module_code → boolean } ─────────────────────────────
// Keys are validated against the 7 module codes.

export const ModuleAccessMapSchema = z.record(ModuleCodeSchema, z.boolean());
export type ModuleAccessMap = z.infer<typeof ModuleAccessMapSchema>;

// ── Role item (from DynamoDB ROLE table) ─────────────────────────────────────

export const RoleDefinitionSchema = z.object({
  role_code: SystemRoleCodeSchema,
  role_name: z.string(),
  role_subtitle: z.string().optional(),
  role_description: z.string().optional(),
  applicable_scope: z.enum(['HOSPITAL', 'ENTERPRISE']).optional(),
  is_active: z.boolean().optional(),
  is_default: z.boolean().optional(),
  is_matrix_editable: z.boolean().optional(),
  default_module_access: ModuleAccessMapSchema,
  permissions_set: z.record(z.string(), z.unknown()).optional(),
});
export type RoleDefinition = z.infer<typeof RoleDefinitionSchema>;

// ── Full matrix: { role_code → { module_code → boolean } } ──────────────────

export const RoleModuleMatrixSchema = z.record(SystemRoleCodeSchema, ModuleAccessMapSchema);
export type RoleModuleMatrix = z.infer<typeof RoleModuleMatrixSchema>;

// ── GET response for role-module-matrix endpoint ─────────────────────────────

export const RoleModuleMatrixResponseSchema = z.object({
  hospital_id: uuidSchema,
  matrix: RoleModuleMatrixSchema,
  source: z.enum(['hospital', 'default']),
  updated_by: z.string().optional(),
  updated_at: z.string().optional(),
});
export type RoleModuleMatrixResponse = z.infer<typeof RoleModuleMatrixResponseSchema>;

// ── PUT request body for saving/updating role-module-matrix ──────────────────

export const SaveRoleModuleMatrixRequestSchema = z.object({
  matrix: RoleModuleMatrixSchema,
  updated_by: uuidSchema,
});
export type SaveRoleModuleMatrixRequest = z.infer<typeof SaveRoleModuleMatrixRequestSchema>;
